import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  CalendarDays,
  Headphones,
  FileText,
  Activity,
  LogOut,
  Bell,
  Shield,
  DollarSign,
  CreditCard,
  Radio,
  Megaphone,
  Mail,
  AppWindow,
  Share2,
  Briefcase,
  Palette,
  Puzzle,
  Key,
  UsersRound,
} from 'lucide-react';
import { isAdminAuthenticated, adminAuthApi } from '../services/adminApi';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useAdminNotificationStore } from '../stores/adminNotificationStore';
import { useAdminSSE } from '../hooks/useAdminSSE';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

// Nav section with grouped items
interface NavSection {
  label: string;
  items: Array<{
    path: string;
    label: string;
    icon: React.ElementType;
  }>;
}

const navSections: NavSection[] = [
  {
    label: 'OVERVIEW',
    items: [
      { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/admin/revenue', label: 'Revenue', icon: DollarSign },
      { path: '/admin/credits', label: 'AI Credits', icon: CreditCard },
    ],
  },
  {
    label: 'GROWTH',
    items: [
      { path: '/admin/email-marketing', label: 'Email Marketing', icon: Mail },
      { path: '/admin/popups', label: 'Popups & Forms', icon: AppWindow },
    ],
  },
  {
    label: 'SOCIAL MEDIA',
    items: [
      { path: '/admin/social-automator', label: 'Social Automator', icon: Share2 },
    ],
  },
  {
    label: 'CORE',
    items: [
      { path: '/admin/business-hub', label: 'Business Hub', icon: Briefcase },
      { path: '/admin/brand-dna', label: 'Brand DNA', icon: Palette },
    ],
  },
  {
    label: 'MANAGEMENT',
    items: [
      { path: '/admin/tenants', label: 'Tenants', icon: Users },
      { path: '/admin/users', label: 'Users', icon: UserCheck },
      { path: '/admin/team', label: 'Team (Admins)', icon: UsersRound },
      { path: '/admin/support', label: 'Support', icon: Headphones },
    ],
  },
  {
    label: 'PLATFORM',
    items: [
      { path: '/admin/integrations', label: 'Integrations', icon: Puzzle },
      { path: '/admin/api-keys', label: 'API Keys', icon: Key },
      { path: '/admin/marketing', label: 'Marketing', icon: Megaphone },
      { path: '/admin/calendar', label: 'Calendar', icon: CalendarDays },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { path: '/admin/logs', label: 'Audit Logs', icon: FileText },
      { path: '/admin/health', label: 'Health', icon: Activity },
      { path: '/admin/monitoring', label: 'Monitoring', icon: Radio },
    ],
  },
];

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // SSE connection for real-time notifications
  useAdminSSE();

  const { notifications, unreadCount, sseConnected, markAsRead, markAllRead } =
    useAdminNotificationStore();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    const fetchAdminInfo = async () => {
      try {
        const { admin: adminData } = await adminAuthApi.me();
        setAdmin(adminData);
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminInfo();
  }, [navigate]);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    adminAuthApi.logout();
    navigate('/admin/login');
  };

  const isActive = (path: string) => location.pathname === path;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <BrandLogo size="md" />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
          <p className="text-gray-500">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 text-gray-900 flex overflow-hidden">
      {/* Sidebar — matches main BuzzBit app sidebar */}
      <div className="w-72 bg-[#F7F7FB] border-r border-gray-200 flex flex-col shrink-0">
        {/* Logo + Admin badge */}
        <div className="px-5 py-5 border-b border-gray-200">
          <BrandLogo size="md" />
          <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-900 text-white">
            Admin
          </span>
        </div>

        {/* Navigation sections */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {navSections.map((section) => (
            <div key={section.label}>
              <p className="px-3 mb-2 text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                {section.label}
              </p>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        active
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`w-[18px] h-[18px] ${active ? 'text-gray-900' : 'text-gray-400'}`} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Admin info footer */}
        <div className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-white">
                {admin?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{admin?.name}</p>
              <p className="text-xs text-gray-500 truncate">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span
              className={`px-2 py-1 rounded font-medium ${
                admin?.role === 'SUPER_ADMIN'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {admin?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Support'}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Shield className="w-4 h-4 text-gray-500" />
            <span>All actions are logged and audited</span>
          </div>

          <div className="flex items-center space-x-4">
            {/* SSE connection indicator */}
            <div className="flex items-center space-x-1.5">
              <div
                className={`w-2 h-2 rounded-full ${
                  sseConnected ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              />
              <span className="text-xs text-gray-400">
                {sseConnected ? 'Live' : 'Offline'}
              </span>
            </div>

            {/* Notification bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-gray-900 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification dropdown */}
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-xs text-gray-900 hover:text-gray-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((n) => (
                        <button
                          key={n.id}
                          onClick={() => markAsRead(n.id)}
                          className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                            !n.read ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-2">
                            {!n.read && (
                              <div className="w-2 h-2 rounded-full bg-gray-900 mt-1.5 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {n.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                {n.message}
                              </p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span
                                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                    n.urgency === 'critical'
                                      ? 'bg-red-50 text-red-700'
                                      : n.urgency === 'high'
                                      ? 'bg-orange-50 text-orange-700'
                                      : n.urgency === 'medium'
                                      ? 'bg-yellow-50 text-yellow-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {n.urgency}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {formatRelative(n.timestamp)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

function formatRelative(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}
